import { useContext, useMemo } from 'react';
import { settingsSection, SettingsItem } from '../../components/MapRightDrawer/SettingsSection';
import { DrawerItem } from '../../components/TGDrawer/TGDrawer';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import { UseConditionalImports } from '../useConditionalImports';
import { colors } from '../../styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPieSimple, faCloudArrowUp, faCircleInfo, faHouse, faUserGroup, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { AssetsDashboardContext, pages } from '../../contexts/assetsDashboardContext';
import { useTheme } from '@material-ui/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ConditionalNavigationButtons, UseConditionalNavigations } from '../useConditionalNavigations';
import { useRecoilValue } from 'recoil';
import { configState } from '../../recoil/atoms';

export interface UseSettingsSection {
  section: DrawerItem;
}

export interface UseSettingsSectionArgs {
  name: string;
  email: string;
}

export const useSettingsSection = (props: UseSettingsSectionArgs): UseSettingsSection => {
  const { buttons } = UseConditionalImports();
  const { navigations } = UseConditionalNavigations();
  const theme = useTheme();
  const { isAnalyticsUser, isAdmin, hasAdvancedLoggingAccess, hasDataExportTrackingAccess } = useUserInfo();
  const { isMobileView, setCurrentPage, currentPage, setOpenDrawer, openDrawer } = useContext(AssetsDashboardContext);
  const uiConfig = useRecoilValue(configState);
  const analyticsEnabled = useMemo(() => uiConfig.enabledFeatures?.analyticsModule, [uiConfig]);
  const styles = {
    menuIcon: {
      color: colors.black25,
      height: 22,
      width: 22,
    },
    activeMenuIcon: {
      color: theme.palette.primary.main,
    },
    drawerIcon: {
      color: colors.black54,
      height: 18,
      width: 18,
    },
  };
  const handleMenuClick = (page: pages, link?: string) => {
    if (link) {
      window.location.href = link;
    }
    setCurrentPage(page);
    setOpenDrawer(false);
  };

  const items: SettingsItem[] = useMemo(() => {
    const menuIcon = (isActive: boolean, icon: IconProp) => {
      return (
        <FontAwesomeIcon
          icon={icon}
          style={{
            ...styles.menuIcon,
            ...(isActive ? styles.activeMenuIcon : {}),
            ...(openDrawer ? styles.drawerIcon : {}),
          }}
        />
      );
    };

    const renderItems = [
      {
        title: 'Home',
        key: 'home',
        visible: true,
        icon: (isActive: boolean) => menuIcon(isActive, faHouse),
        subMenu: undefined,
      },
      {
        title: 'Users & Roles',
        key: 'usersAndRoles',
        visible: isAdmin,
        icon: (isActive: boolean) => menuIcon(isActive, faUserGroup),
        subMenu: undefined,
      },
      {
        title: 'Analytics',
        key: 'analytics',
        visible: isAnalyticsUser && analyticsEnabled && !isMobileView,
        icon: (isActive: boolean) => menuIcon(isActive, faChartPieSimple),
        subMenu: undefined,
      },
      {
        title: 'Import',
        key: 'import',
        visible: buttons && buttons.length && !isMobileView,
        icon: (isActive: boolean) => menuIcon(isActive, faCloudArrowUp),
        subMenu: undefined,
      },
      {
        title: 'About',
        key: 'about',
        visible: true,
        icon: (isActive: boolean) => menuIcon(isActive, faCircleInfo),
        subMenu: undefined,
      },
      {
        title: 'Logs',
        key: 'logs',
        visible: hasAdvancedLoggingAccess || hasDataExportTrackingAccess,
        icon: (isActive: boolean) => menuIcon(isActive, faFileLines),
        subMenu: undefined,
      }
    ];

    const mapConditionalNavigationToSettingsItems = (
      navigationButtons: ConditionalNavigationButtons,
      currentPage: string,
      handleMenuClick: (page: pages, link?: string) => void
    ): SettingsItem[] => {
      const { order, components } = navigationButtons;

      return order.map(key => {
        const item = components[key];
        const itemIcon = (isActive: boolean) => menuIcon(isActive, item.icon as IconProp);
        return {
          title: item.label,
          icon: itemIcon(currentPage === key),
          clickHandler: () => handleMenuClick(item.label as pages, item.link),
          isActive: currentPage === key,
          subMenu: item.subMenu && mapConditionalNavigationToSettingsItems(item.subMenu, currentPage, handleMenuClick),
        };
      });
    };

    const results: SettingsItem[] = [];

    renderItems.map(item => {
      if (item.visible) {
        results.push({
          title: item.title,
          icon: item.icon(currentPage === item.key),
          clickHandler: () => handleMenuClick(item.key as pages),
          isActive: item.key === currentPage,
        });
      }
    });
    const conditionalNavItems =
      (navigations && mapConditionalNavigationToSettingsItems(navigations, currentPage, handleMenuClick)) || [];

    return [...results, ...conditionalNavItems];
  }, [
    buttons,
    isAdmin,
    isAnalyticsUser,
    analyticsEnabled,
    currentPage,
    navigations,
  ]);

  const section = settingsSection({
    ...props,
    items,
  });
  return {
    section,
  };
};
